import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, Check, ChevronDown } from "lucide-react";
import { SERVICE_META } from "@/data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAQS = [
  { q: "How does the deposit work?", a: "You pay a small per-service deposit when booking to reserve your slot. It's applied toward your final bill — the remaining balance is paid after the job is complete." },
  { q: "Where do you perform the service?", a: "Anywhere you are within the Greater Charlotte Metro Area — your home driveway, workplace parking lot, or roadside. Just give us safe access to the vehicle." },
  { q: "What if I need to reschedule?", a: "Reach out at least 12 hours before your slot and we'll move your booking and deposit to a new time at no cost. If you wish to cancel you must do so 24 hours in advance to be eligible for ae refund on your deposit." },
];

export default function Services() {
  const [services, setServices] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="services-page" className="pt-32">
      <section className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-400">Our Services</span>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter text-white mt-4 leading-none">
            Pro Service, <span className="text-yellow-400">No Garage.</span>
          </h1>
          <p className="mt-5 text-lg text-neutral-300 leading-relaxed">
            Transparent pricing, certified mechanics, and a deposit that's always applied to your final bill.
          </p>
        </div>
      </section>

      {/* SERVICE DETAIL LIST */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-16 space-y-4">
        {services.map((s, i) => {
          const meta = SERVICE_META[s.id] || {};
          const Icon = meta.icon;
          return (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="group grid grid-cols-1 lg:grid-cols-12 gap-0 bg-[#141414] border border-neutral-800 rounded-sm overflow-hidden hover:border-yellow-400 transition-colors"
              data-testid={`service-row-${s.id}`}
            >
              <div className="lg:col-span-4 h-56 lg:h-auto relative overflow-hidden">
                <img src={meta.image} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-[#141414] via-transparent to-transparent" />
              </div>

              <div className="lg:col-span-5 p-8">
                <div className="flex items-center gap-3 mb-3">
                  {Icon && (
                    <div className="w-10 h-10 bg-neutral-900 border border-neutral-800 rounded-sm flex items-center justify-center">
                      <Icon className="w-5 h-5 text-yellow-400" />
                    </div>
                  )}
                  <h2 className="font-heading font-bold text-2xl text-white">{s.name}</h2>
                </div>
                <p className="text-neutral-400 leading-relaxed mb-5">{s.tagline}</p>
                <ul className="grid grid-cols-2 gap-2">
                  {(meta.features || []).map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-neutral-300">
                      <Check className="w-4 h-4 text-yellow-400 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="lg:col-span-3 p-8 border-t lg:border-t-0 lg:border-l border-neutral-800 flex flex-col justify-center">
                <span className="text-xs uppercase tracking-wider text-neutral-500">From</span>
                <span className="font-heading font-extrabold text-4xl text-white">${s.price.toFixed(0)}</span>
                <div className="flex items-center gap-2 mt-1 mb-4 text-sm">
                  <span className="text-yellow-400 font-mono font-semibold">${s.deposit.toFixed(0)} deposit</span>
                  <span className="text-neutral-600">·</span>
                  <span className="text-neutral-500">{s.duration}</span>
                </div>
                <button
                  data-testid={`book-service-${s.id}`}
                  onClick={() => navigate(`/book?service=${s.id}`)}
                  className="inline-flex items-center justify-center gap-2 bg-yellow-400 text-black font-bold uppercase text-sm tracking-wide px-5 py-3 rounded-sm hover:bg-yellow-500 transition-colors"
                >
                  Book This <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          );
        })}
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-24">
        <div className="text-center mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-400">Good To Know</span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl uppercase tracking-tight text-white mt-3">FAQ</h2>
        </div>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <div key={f.q} className="bg-[#141414] border border-neutral-800 rounded-sm overflow-hidden" data-testid={`faq-${i}`}>
              <button
                className="w-full flex items-center justify-between gap-4 p-5 text-left"
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                data-testid={`faq-toggle-${i}`}
              >
                <span className="font-bold text-white">{f.q}</span>
                <ChevronDown className={`w-5 h-5 text-yellow-400 shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && (
                <p className="px-5 pb-5 text-neutral-400 leading-relaxed">{f.a}</p>
              )}
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
